/* Wat het CRM-dashboard leest. Elk stuk is een kleine, losse query; een tabel die
 * de gebruiker niet mag lezen geeft nul of een lege lijst, geen fout. De client
 * komt als argument binnen, zodat de toetsen hem kunnen nabootsen. */

import { faseVanStadium, type KavelFaseOfRest } from '@/lib/chargingStages';

export interface DashboardClient {
  from(tabel: string): any;
  schema(naam: string): { from(tabel: string): any };
}

/** YYYY-MM-DD in de tijdzone van de browser; UTC zou 's avonds laat een dag te vroeg 'te laat' melden. */
const lokaleDag = (d: Date) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const vandaag = (nu: Date) => lokaleDag(nu);
const plusDagen = (nu: Date, d: number) => lokaleDag(new Date(nu.getTime() + d * 86_400_000));

async function tel(q: PromiseLike<{ count: number | null; error: unknown }>): Promise<number> {
  try {
    const r = await q;
    return r.error ? 0 : r.count ?? 0;
  } catch {
    return 0;
  }
}

async function rijen<T>(q: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  try {
    const r = await q;
    return r.error ? [] : r.data ?? [];
  } catch {
    return [];
  }
}

/* ── aandacht ─────────────────────────────────────────────────────── */

export interface Aandacht {
  mailSuggesties: number;
  mailOnverwerkt: number;
  takenOpen: number;
  takenTeLaat: number;
  mijlpalenTeLaat: number;
  mijlpalenKomend: number;
  offertesConcept: number;
  /** Verstuurd en nog geen besluit: de klant is aan zet. */
  offertesWachtOpKlant: number;
  /** Open (concept of verstuurd) en geldig tot binnen 7 dagen, vandaag meegeteld. */
  offertesVerlooptDezeWeek: number;
  /** Open en geldig tot vóór vandaag; afgeleid, nooit opgeslagen (ADR 0018). */
  offertesVerlopen: number;
}

/** Mijn taken: aan mij toegewezen, of een CRM-teamtaak zonder eigenaar. Zelfde filter voor tegel en lijst. */
export const mijnTakenFilter = (userId: string) => `assigned_to.eq.${userId},and(scope.eq.team_crm,assigned_to.is.null)`;

export async function haalAandacht(client: DashboardClient, userId: string, nu: Date = new Date()): Promise<Aandacht> {
  const kop = { count: 'exact' as const, head: true };
  const dag = vandaag(nu);
  const openOffertes = () => client.from('quotes').select('id', kop).in('status', ['draft', 'sent']).is('deleted_at', null);
  const [
    mailSuggesties, mailOnverwerkt, takenOpen, takenTeLaat, mijlpalenTeLaat, mijlpalenKomend,
    offertesConcept, offertesWachtOpKlant, offertesVerlooptDezeWeek, offertesVerlopen,
  ] =
    await Promise.all([
      tel(client.from('email_inbox').select('id', kop).eq('processing_status', 'suggestions_ready')),
      tel(client.from('email_inbox').select('id', kop).eq('processing_status', 'pending')),
      tel(client.from('unified_todos').select('id', kop).neq('status', 'completed').or(mijnTakenFilter(userId))),
      tel(client.from('unified_todos').select('id', kop).neq('status', 'completed').lt('due_date', dag).or(mijnTakenFilter(userId))),
      tel(client.from('project_milestones').select('id', kop).neq('status', 'completed').lt('milestone_date', dag)),
      tel(client.from('project_milestones').select('id', kop).neq('status', 'completed').gte('milestone_date', dag).lte('milestone_date', plusDagen(nu, 30))),
      tel(client.from('quotes').select('id', kop).eq('status', 'draft').is('deleted_at', null)),
      tel(client.from('quotes').select('id', kop).eq('status', 'sent').is('deleted_at', null)),
      tel(openOffertes().gte('valid_until', dag).lte('valid_until', plusDagen(nu, 7))),
      tel(openOffertes().lt('valid_until', dag)),
    ]);
  return {
    mailSuggesties, mailOnverwerkt, takenOpen, takenTeLaat, mijlpalenTeLaat, mijlpalenKomend,
    offertesConcept, offertesWachtOpKlant, offertesVerlooptDezeWeek, offertesVerlopen,
  };
}

/* ── projecten voor pijplijn en actieve lijst ─────────────────────── */

export interface DashboardProject {
  id: string;
  name: string;
  project_number: string | null;
  stage: string | null;
  status: string | null;
  amount: number | null;
  probability: number | null;
  updated_at: string | null;
  created_at: string;
  org_id: string | null;
  organization: { id: string; name: string } | null;
}

export async function haalProjecten(client: DashboardClient): Promise<DashboardProject[]> {
  const { data, error } = await client
    .from('projects')
    .select('id, name, project_number, stage, status, amount, probability, updated_at, created_at, org_id, organization:organizations(id, name)')
    .is('deleted_at', null);
  if (error) throw error;
  return (data ?? []).map((p: any) => ({
    ...p,
    amount: p.amount === null || p.amount === undefined ? null : Number(p.amount),
    organization: Array.isArray(p.organization) ? p.organization[0] ?? null : p.organization ?? null,
  }));
}

/* ── mijn taken ───────────────────────────────────────────────────── */

export interface Taak {
  id: string;
  title: string;
  due_date: string | null;
  priority: string | null;
  project_id: string | null;
  location_id: string | null;
  opportunity_id: string | null;
}

/** Open taken van de gebruiker, en teamtaken van het CRM zonder eigenaar; te laat eerst. */
export async function haalMijnTaken(client: DashboardClient, userId: string): Promise<Taak[]> {
  const lijst = await rijen<Taak>(
    client
      .from('unified_todos')
      .select('id, title, due_date, priority, project_id, location_id, opportunity_id')
      .neq('status', 'completed')
      .or(mijnTakenFilter(userId))
      .limit(50),
  );
  return lijst.sort((a, b) => (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999'));
}

export async function rondTaakAf(client: DashboardClient, id: string): Promise<void> {
  const { error } = await client.from('unified_todos').update({ status: 'completed', completed_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

/* ── mijlpalen ────────────────────────────────────────────────────── */

export interface Mijlpaal {
  id: string;
  title: string;
  milestone_date: string;
  status: string | null;
  project_id: string;
}

/** Open mijlpalen tot 30 dagen vooruit, de oudste (en dus meest te late) eerst. */
export async function haalMijlpalen(client: DashboardClient, nu: Date = new Date()): Promise<Mijlpaal[]> {
  return rijen<Mijlpaal>(
    client
      .from('project_milestones')
      .select('id, title, milestone_date, status, project_id')
      .neq('status', 'completed')
      .lte('milestone_date', plusDagen(nu, 30))
      .order('milestone_date', { ascending: true })
      .limit(40),
  );
}

/* ── recent in projecten ──────────────────────────────────────────── */

export type RecentSoort = 'versie' | 'notitie' | 'offerte' | 'mail';

export interface RecentItem {
  id: string;
  soort: RecentSoort;
  project_id: string;
  tijd: string;
  tekst: string;
}

const OFFERTE_STATUS: Record<string, string> = { draft: 'in concept', sent: 'verstuurd', accepted: 'geaccepteerd', declined: 'afgewezen', expired: 'verlopen' };

export async function haalRecent(client: DashboardClient, aantal = 10): Promise<RecentItem[]> {
  const [versies, notities, offertes, mails] = await Promise.all([
    rijen<any>(client.from('project_file_versions').select('id, project_id, file_name, previous_file_name, created_at').order('created_at', { ascending: false }).limit(aantal)),
    rijen<any>(client.from('project_notes').select('id, project_id, title, created_at').order('created_at', { ascending: false }).limit(aantal)),
    rijen<any>(client.from('quotes').select('id, project_id, quote_number, title, status, updated_at').not('project_id', 'is', null).is('deleted_at', null).order('updated_at', { ascending: false }).limit(aantal)),
    rijen<any>(client.from('email_inbox').select('id, matched_project_id, subject, received_at').not('matched_project_id', 'is', null).order('received_at', { ascending: false }).limit(aantal)),
  ]);
  const versieNotities = new Set(versies.map((v) => `Nieuwe versie: ${v.file_name}`));
  const alles: RecentItem[] = [
    ...versies.map((v) => ({ id: `v:${v.id}`, soort: 'versie' as const, project_id: v.project_id, tijd: v.created_at, tekst: v.previous_file_name ? `Nieuwe versie: ${v.file_name}` : `Bestand: ${v.file_name}` })),
    ...notities.filter((n) => !versieNotities.has(n.title)).map((n) => ({ id: `n:${n.id}`, soort: 'notitie' as const, project_id: n.project_id, tijd: n.created_at, tekst: n.title })),
    ...offertes.map((q) => ({
      id: `q:${q.id}`, soort: 'offerte' as const, project_id: q.project_id, tijd: q.updated_at,
      tekst: `Offerte ${[q.quote_number, q.title].filter(Boolean).join(' · ')} ${OFFERTE_STATUS[q.status] ?? q.status}`,
    })),
    ...mails.map((m) => ({ id: `m:${m.id}`, soort: 'mail' as const, project_id: m.matched_project_id, tijd: m.received_at, tekst: m.subject || '(geen onderwerp)' })),
  ].filter((i) => i.project_id && i.tijd);
  return alles.sort((a, b) => (a.tijd < b.tijd ? 1 : a.tijd > b.tijd ? -1 : 0)).slice(0, aantal);
}

/* ── live installaties ────────────────────────────────────────────── */

export interface LiveLocatie {
  id: string;
  naam: string;
  plaats: string | null;
  project_id: string | null;
  /** De Site waar deze meetlocatie aan hangt (ems.locatie.site_id); Locaties toont de stand per site. */
  site_id: string | null;
  verbruikKw: number | null;
  netKw: number | null;
  zonKw: number | null;
  batterijProcent: number | null;
  laatste: string | null;
}

export async function haalLive(client: DashboardClient): Promise<LiveLocatie[]> {
  const locaties = await rijen<any>(client.schema('ems').from('locatie').select('id, naam, plaats, project_id, site_id').eq('actief', true).order('naam'));
  if (!locaties.length) return [];
  const ids = locaties.map((l) => l.id);
  const [assets, stand, sites] = await Promise.all([
    rijen<any>(client.schema('ems').from('asset').select('id, locatie_id, type, capaciteit_kwh').in('locatie_id', ids).eq('actief', true)),
    rijen<any>(client.schema('ems').from('v_laatste_stand').select('locatie_id, asset_id, veld, waarde, tijdstip').in('locatie_id', ids)),
    rijen<any>(client.from('sites').select('id, project_id').in('id', locaties.map((l) => l.site_id).filter(Boolean))),
  ]);
  const projectVanSite = new Map(sites.map((s) => [s.id, s.project_id]));
  return locaties.map((l) => {
    const eigen = stand.filter((s) => s.locatie_id === l.id && s.waarde !== null);
    const meters = new Set(assets.filter((a) => a.locatie_id === l.id && a.type === 'netmeter').map((a) => a.id));
    const kasten = assets.filter((a) => a.locatie_id === l.id && a.type === 'batterij');
    const kastIds = new Set(kasten.map((a) => a.id));
    const som = (ids: Set<string>, veld: string) => {
      const r = eigen.filter((s) => ids.has(s.asset_id) && s.veld === veld);
      return r.length ? r.reduce((t, s) => t + Number(s.waarde), 0) : null;
    };
    const imp = som(meters, 'net_import');
    const exp = som(meters, 'net_export');
    const energie = som(kastIds, 'bat_energie');
    const capaciteit = kasten.reduce((t, a) => t + (Number(a.capaciteit_kwh) || 0), 0);
    return {
      id: l.id,
      naam: l.naam,
      plaats: l.plaats ?? null,
      project_id: l.project_id ?? projectVanSite.get(l.site_id) ?? null,
      site_id: l.site_id ?? null,
      verbruikKw: som(meters, 'verbruik'),
      netKw: imp === null && exp === null ? null : (imp ?? 0) - (exp ?? 0),
      zonKw: som(meters, 'zon_locatie'),
      batterijProcent: energie !== null && capaciteit > 0 ? (energie / capaciteit) * 100 : null,
      laatste: eigen.map((s) => String(s.tijdstip)).sort().at(-1) ?? null,
    };
  });
}

/* ── Charging ─────────────────────────────────────────────────────── */

export interface ChargingStand {
  kavels: Record<KavelFaseOfRest, number>;
  opportunitiesLopend: number;
  takenTeLaat: number;
}

export async function haalCharging(client: DashboardClient, nu: Date = new Date()): Promise<ChargingStand> {
  const [kavels, opps, teLaat] = await Promise.all([
    rijen<{ stage: string | null }>(client.from('charging_locations').select('stage').is('deleted_at', null)),
    rijen<{ status: string | null }>(client.from('charging_opportunities').select('status').is('deleted_at', null)),
    tel(client.from('unified_todos').select('id', { count: 'exact', head: true }).in('scope', ['location', 'opportunity']).neq('status', 'completed').lt('due_date', vandaag(nu))),
  ]);
  const fasen: Record<KavelFaseOfRest, number> = { acquisitie: 0, realisatie: 0, operatie: 0, verloren: 0, onbekend: 0 };
  for (const k of kavels) fasen[faseVanStadium(k.stage)]++;
  return {
    kavels: fasen,
    opportunitiesLopend: opps.filter((o) => o.status !== 'gewonnen' && o.status !== 'verloren').length,
    takenTeLaat: teLaat,
  };
}
